import React, { useState } from "react";
import { NextSeo } from "next-seo";
import { AppSidebar } from "@/components/dashboard/SimpleSidebar";
import { Menu, X } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Link from "next/link";
import { Button } from "@/components/ui/Button";

interface DashboardLayoutProps {
  activePath: string;
  children: React.ReactNode;
  title?: string;
  noindex?: boolean;
  nofollow?: boolean;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
  }>;
}

export default function DashboardLayout({
  activePath,
  children,
  title,
  noindex = true,
  nofollow = true,
  breadcrumbs,
}: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      {title && <NextSeo title={title} noindex={noindex} nofollow={nofollow} />}
      <div className="flex min-h-screen w-full">
        {/* Desktop Sidebar */}
        <AppSidebar activePath={activePath} />

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <AppSidebar
              activePath={activePath}
              className="fixed left-0 top-0 bottom-0 w-64 flex bg-sidebar-background"
            />
          </div>
        )}

        <div className="flex-1 flex flex-col">
          <header className="flex h-16 items-center gap-2 border-b bg-background px-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link href="/dashboard">Dashboard</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        {crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link href={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </header>
          <main className="flex-1">{children}</main>
        </div>
      </div>
    </>
  );
}
